
import { defineComponent, ref, onMounted, onUnmounted, PropType } from 'vue';
import { ElMain, ElHeader, ElDrawer, ElDialog } from 'element-plus';
import { getColStyle, getColClassList } from './utils';
import { ArrowDown, ArrowUp } from '@element-plus/icons-vue';

type TElDrawer = typeof ElDrawer;
type IElDrawer = {
  withHeader: boolean;
} & TElDrawer;

type TElDialog = typeof ElDialog;
type IElDialog = {
  visible: boolean;
  title: any;
  footer: any;
} & TElDialog;

// interface IFheader {
//   showCollapse: boolean;
//   col: typeof ElCol;
//   row: typeof ElRow;
// }

// interface IPropsType {
//   laside: typeof ElAside;
//   raside: typeof ElAside;
//   fheader: IFheader;
//   dialog: { [key: string]: IElDialog; };
//   drawer: { [key: string]: IElDrawer; };
// }

export default defineComponent({
  name: 'AppPage',
  props: {
    laside: {
      type: Object,
      default() {
        return {};
      },
    },
    raside: {
      type: Object,
      default() {
        return {};
      },
    },
    fheader: {
      type: Object,
      default() {
        return {};
      },
    },
    dialog: {
      type: Object as PropType<{ [key: string]: IElDialog; }>,
      default() {
        return {};
      },
    },
    drawer: {
      type: Object as PropType<{ [key: string]: IElDrawer; }>,
      default() {
        return {};
      },
    },
  },
  components: {
    ArrowDown,
    ArrowUp,
  },
  setup() {
    const contentHeight = ref(0);
    const collapse = ref(false);

    const contentMainRef = ref<typeof ElMain | null>(null);
    const contentHeaderRef = ref<typeof ElHeader | null>(null);
    const contentWrapperRef = ref<typeof ElMain | null>(null);

    const computedContentHeight = () => {
      if (contentWrapperRef.value && contentMainRef.value) {
        const height = contentWrapperRef.value.$el.clientHeight;
        const { paddingTop, paddingBottom } = getComputedStyle(contentMainRef.value.$el);
        let headerHeight = 0;
        if (contentHeaderRef.value) {
          headerHeight = parseInt(getComputedStyle(contentHeaderRef.value.$el).height || '0px', 10);
        }
        contentHeight.value = (
          height - parseInt(paddingTop || '0', 10)
          - parseInt(paddingBottom || '0', 10)
          - headerHeight
        );
      }
    };

    let timer: number | null = null;
    const windowResizeCallBack = () => {
      if (timer) return;
      timer = window.setTimeout(() => {
        timer = null;
        computedContentHeight();
      }, 600);
    };

    const handleCollapse = () => {
      collapse.value = !collapse.value;
      windowResizeCallBack();
    };

    onMounted(() => {
      computedContentHeight();
      window.addEventListener('resize', windowResizeCallBack, false);
    });
    onUnmounted(() => {
      window.removeEventListener('resize', windowResizeCallBack, false);
    });

    return {
      contentHeight,
      collapse,

      contentMainRef,
      contentHeaderRef,
      contentWrapperRef,

      computedContentHeight,
      windowResizeCallBack,
      handleCollapse,
    };
  },
  render() {
    const renderCollapse = () => {
      const { showCollapse } = this.fheader;

      return (
        <el-button v-show={showCollapse} onClick={this.handleCollapse}>
          {this.collapse ? '收起' : '展开'}
          {this.collapse ? (
            <el-icon>
              <ArrowUp />
            </el-icon>
          ) : (
            <el-icon>
              <ArrowDown />
            </el-icon>
          )}
        </el-button>
      );
    };

    // 渲染固定头部
    const renderFHeader = () => {
      if (!this.$slots.fheader) return null;

      const { row = {}, col } = this.fheader;
      const fheaderVNode = this.$slots.fheader?.({
        collapse: this.collapse,
        handleCollapse: this.handleCollapse,
      });

      if (fheaderVNode instanceof Array) {
        fheaderVNode.forEach((vNodeItem: any) => {
          const vNodeChildKeys = Object.keys(vNodeItem.children || {});
          if (vNodeItem.type?.name === 'ElForm') {
            vNodeChildKeys.forEach((key) => {
              const _renderFunc = vNodeItem.children[key];

              if (typeof _renderFunc !== 'function') {
                return;
              }

              vNodeItem.children[key] = function renderDef() {
                const children = _renderFunc();
                // 给row的数据
                const rowPropsData = {
                  gutter: 10,
                  ...row,
                };
                const _renderChild = () => {
                  // 这块处理展开按钮
                  {
                    const _getShowCollapseVNode = (vNode: any) => {
                      const _childrenRender = vNode.children.default;
                      vNode.children.default = () => {
                        return [
                          ..._childrenRender(),
                          renderCollapse(),
                        ];
                      };

                      return vNode;
                    };

                    let len = children?.length || 0;
                    while (len > 0) {
                      len -= 1;
                      const lastElFormItemChild = children[len];
                      if (
                          lastElFormItemChild?.type?.name === 'ElFormItem'
                            // 保证最后一个form-item可被正确渲染
                            && typeof lastElFormItemChild?.children?.default === 'function'
                      ) {
                        _getShowCollapseVNode(lastElFormItemChild);
                        break;
                      }
                    }
                  }

                  const colClass = getColClassList(
                    (col || { xs: 24, sm: 12, md: 12, lg: 8, xl: 6 }),
                    rowPropsData,
                  );
                  const colStyle = getColStyle(rowPropsData);

                  return children.map((vNode: any, i: number) => {
                    const _key = `${vNode?.type?.name || ''}-${i}`;
                    return (
                      vNode?.type?.name !== 'ElFormItem'
                        ? vNode
                        : <div
                            class={['el-col', colClass]}
                            style={colStyle}
                            key={_key}
                          >
                          {
                            vNode
                          }
                        </div>
                    );
                  });
                };

                return [
                  (
                    <el-row { ...rowPropsData }>
                      {
                        _renderChild()
                      }
                    </el-row>
                  ),
                ];
              };
            });
          }
        });
      }

      return (
        <el-header class="page-fixed-header">
          {
            fheaderVNode
          }
        </el-header>
      );
    };

    // render Main header
    const renderMainHeader = () => {
      if (!this.$slots.header) return null;
      this.windowResizeCallBack();
      return <el-header class="page-header" ref="contentHeaderRef">
        {this.$slots.header()}
      </el-header>;
    };

    // 渲染主要内容区域
    const renderMain = () => {
      return (
      <el-main class="page-content" ref="contentWrapperRef">
        { renderMainHeader() }
        {this.$slots.main && (
          <el-main
            ref="contentMainRef"
            class="page-main"
          >
            {this.$slots.main({ height: this.contentHeight })}
          </el-main>
        )}
        {this.$slots.footer && (
          <el-footer class="page-footer">{this.$slots.footer()}</el-footer>
        )}
      </el-main>
      );
    };

    // 渲染固定底部
    const renderFFooter = () => {
      if (!this.$slots.ffooter) return null;

      return <el-footer class="page-fixed-footer">{this.$slots.ffooter()}</el-footer>;
    };

    // 渲染左侧边栏
    const renderLAside = () => {
      if (!this.$slots.laside) return null;

      return (
        <el-aside class="page-left-aside" {...{ props: this.laside }}>
          {this.$slots.laside()}
        </el-aside>
      );
    };

    // 渲染右侧边栏
    const renderRAside = () => {
      if (!this.$slots.raside) return null;

      return (
        <el-aside class="page-right-aside" {...{ props: this.raside }}>
          {this.$slots.raside()}
        </el-aside>
      );
    };

    // 渲染 dialog
    const renderDialog = () => {
      return Object.entries(this.dialog).map(
        ([dialogName, { destroyOnClose = true, title, footer, ...dialogProps }]) => {
          return (
            this.$slots[dialogName]
              && this.dialog[dialogName].visible && (
                <el-dialog
                  modelValue={ true }
                  destroyOnClose={ destroyOnClose }
                  {
                    ...{ ...dialogProps }
                  }
                  onClose={() => {
                    const _dialog = { ...this.dialog };
                    _dialog[dialogName].visible = false;
                    this.$emit('update:dialog', {
                      ..._dialog,
                    });
                    // this.dialog[dialogName].visible = false;
                  }}
                  v-slots={{
                    title: () => (
                      <div class="el-dialog__title">
                        {title}
                      </div>
                    ),
                    default: () => (
                      (this.$slots[dialogName] as any)()
                    ),
                    footer: () => footer,
                  }}
                >
                </el-dialog>
            )
          );
        },
      );
    };

    // 渲染Drawer
    const renderDrawer = () => {
      return Object.entries(this.drawer).map(
        ([
          drawerName,
          { withHeader = false, destroyOnClose = true, title, ...dialogProps },
        ]) => {
          delete dialogProps.visible;
          return (
            this.$slots[drawerName]
            && this.drawer[drawerName].visible && (
              <el-drawer
                {...{ withHeader, destroyOnClose, ...dialogProps }}
                v-model={this.drawer[drawerName].visible}
                onClose={() => {
                  const _drawer = { ...this.drawer };
                  _drawer[drawerName].visible = false;
                  this.$emit('update:drawer', {
                    ..._drawer,
                  });
                  // this.drawer[drawerName].visible = false;
                }}
              >
                <div class="page-drawer">
                  {title && <h3 class="page-drawer-title">{title}</h3>}
                  <div class="page-drawer-content">{(this.$slots[drawerName] as any)()}</div>
                </div>
              </el-drawer>
            )
          );
        },
      );
    };

    return (
      <el-card class="app-page" style="height:calc(100vh - 55px);">
        {this.$slots.default && this.$slots.default()}
        <el-container>
          { renderLAside() }
          <el-container class="page-center-container" direction="vertical">
            { renderFHeader() }
            { renderMain() }
            { renderFFooter() }
          </el-container>
          { renderRAside() }
          { renderDialog() }
          { renderDrawer() }
        </el-container>
      </el-card>
    );
  },
});
